import { useState, useEffect } from 'react';
import type { AxiosInstance } from 'axios';
import type { BackboneService } from '../backbone.types';
import {
    fetchBackboneList,
    mapBbaasItemToBackboneService,
} from '../requests/request';
import { readCachedFetch, writeCachedFetch } from '@hooks/useCachedFetch';

/**
 * Load backbone (BBAAS) detail by id from the tenant list:
 * GET /kontroledge/bbaas?tenantId=&limit=&offset=
 */
export function useBackboneDetails(
    backboneId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    // The last answer for this backbone paints at once when the user comes back (a view
    // switch, Back); the request below still runs and replaces it.
    const cacheKey =
        backboneId && tenantId != null
            ? `kontroledge/bbaas/${backboneId}?tenantId=${tenantId}`
            : null;
    const [raw, setRaw] = useState<BackboneService | null>(
        () => readCachedFetch<BackboneService>(cacheKey) ?? null
    );
    const [loading, setLoading] = useState(() => !readCachedFetch(cacheKey));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!backboneId) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }
        if (tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        // Another backbone on the same mounted page: its own cached answer or the
        // skeleton — never the previous backbone's data.
        const cached = readCachedFetch<BackboneService>(cacheKey);
        setRaw(cached ?? null);
        setLoading(!cached);
        setError(null);

        (async () => {
            try {
                const response = await fetchBackboneList(api, tenantId, {
                    limit: 100,
                    offset: 0,
                });
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !Array.isArray(body.data)) {
                    throw new Error(body?.message ?? 'Failed to load backbone');
                }
                const item = body.data.find((d) => String(d.id) === String(backboneId));
                if (!item) {
                    setRaw(null);
                    setError('Backbone not found');
                    return;
                }
                const mapped = mapBbaasItemToBackboneService(item);
                writeCachedFetch(cacheKey, mapped);
                setRaw(mapped);
                setError(null);
            } catch (e) {
                console.error('Error in useBackboneDetails:', e);
                // A cached answer on screen stays there; the next visit retries.
                if (!cancelled && !cached) {
                    setRaw(null);
                    setError(e instanceof Error ? e.message : 'Failed to load backbone');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [backboneId, tenantId, api, refresh, cacheKey]);

    return { raw, loading, error };
}
